import * as React from 'react';
import * as classNames from 'classnames';
import { DrawingMode, ShapeKind, LineType } from '../../drawing/types';
import {
  DrawingToolState, initVisibleShapeKinds, DrawingAttr,
} from '../../drawing/reducers/drawingTool';
import MdcIconButton from '../common/MdcIconButton';
import ButtonSpacer from '../common/ButtonSpacer';
import MdcMenu from '../common/MdcMenu';
import DrawingToolAction from '../../drawing/actions/DrawingToolAction';

import { shadowElevationZ2 } from '../../style/effects';
import {
  toolbarTextColor, drawingToolbarHeight, drawingToolbarBackgroundColor,
} from '../../style/values';

interface DrawingToolbarProps {
  toolState: DrawingToolState;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onToolAction: (action: DrawingToolAction.Action) => void;
  onClearDrawing: () => void;
}

interface DrawingToolbarState {
  showFiguresMenu: boolean;
  showDrawingVisibilityMenu: boolean;
}

function createRandomColor(): string {
  // https://stackoverflow.com/questions/1484506/random-color-generator
  const letters: string = '0123456789ABCDEF';
  let randomColor: string = '#';
  for (let i = 0; i < 6; i += 1) {
    randomColor += letters[Math.floor(Math.random() * 16)];
  }
  return randomColor;
}

function convertDrawingModeToShapeKinds(mode: DrawingMode): ShapeKind[] | null {
  switch (mode) {
    case DrawingMode.RectDrawing:
      return [ShapeKind.Rect];
    case DrawingMode.SquareDrawing:
      return [ShapeKind.Square];
    case DrawingMode.OvalDrawing:
      return [ShapeKind.Oval];
    case DrawingMode.CircleDrawing:
      return [ShapeKind.Circle];
    case DrawingMode.TriangleDrawing:
      return [ShapeKind.Triangle];
    case DrawingMode.EquilateralTriangleDrawing:
      return [ShapeKind.EquilateralTriangle];
    case DrawingMode.StickyDrawing:
      return [ShapeKind.Sticky];
    case DrawingMode.BalloonDrawing:
      return [ShapeKind.Balloon];
    case DrawingMode.StraightLineDrawing:
      return [ShapeKind.StraightLine];
    case DrawingMode.AxisAlignedStraightLineDrawing:
      return [ShapeKind.AxisAlignedStraightLine];
    case DrawingMode.MultiVertexDrawing:
      return [ShapeKind.Polygon, ShapeKind.Polyline];
    case DrawingMode.ImagePasting:
      return [ShapeKind.ImageFile];
    default:
      return null;
  }
}

const toolbarStyle: React.CSSProperties = {
  ...shadowElevationZ2,
  position: 'relative',
  height: `${drawingToolbarHeight}px`,
  color: toolbarTextColor,
  backgroundColor: drawingToolbarBackgroundColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
};

const sectionStyle: React.CSSProperties = {
  display: 'inline-flex',
  flex: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

class DrawingToolbar extends React.PureComponent<DrawingToolbarProps, DrawingToolbarState> {
  private imageFileInput: HTMLInputElement | null;

  public constructor(props: Readonly<DrawingToolbarProps>) {
    super(props);
    this.imageFileInput = null;
    this.state = { showFiguresMenu: false, showDrawingVisibilityMenu: false };
  }

  private setMode(newMode: DrawingMode) {
    // 描いたものが見えないと意味がないので描こうとするものは強制的に表示する
    if (newMode === DrawingMode.Handwriting && !this.isHandwritingVisible()) {
      this.toggleHandwritingVisibility();
    }
    const modeShapeKinds = convertDrawingModeToShapeKinds(newMode);
    if (modeShapeKinds !== null) {
      this.changeVisibilities(modeShapeKinds, true);
    }

    this.props.onToolAction(DrawingToolAction.setMode(newMode));
  }

  private confirmClearEverything() {
    if (window.confirm('このページの書き込みをすべて削除します。本当によろしいですか？')) {
      this.props.onClearDrawing();
    }
  }

  private toggleVisibility(shapeKind: ShapeKind) {
    this.changeVisibilities([shapeKind], !this.isVisible(shapeKind));
  }

  private isVisible(shapeKind: ShapeKind): boolean {
    return this.props.toolState.visibleShapeKinds.indexOf(shapeKind) !== -1;
  }

  private toggleHandwritingVisibility() {
    const newShowHandwriting = !this.props.toolState.showHandwriting;
    this.props.onToolAction(DrawingToolAction.setShowHandwriting(newShowHandwriting));
  }

  private isHandwritingVisible(): boolean {
    return this.props.toolState.showHandwriting;
  }

  private toggleAllDrawingVisibility() {
    if (this.isVisibleDrawingNothing()) {
      this.props.onToolAction(DrawingToolAction.setVisibleShapeKinds(initVisibleShapeKinds()));
      this.props.onToolAction(DrawingToolAction.setShowHandwriting(true));
    } else {
      this.props.onToolAction(DrawingToolAction.setVisibleShapeKinds([]));
      this.props.onToolAction(DrawingToolAction.setShowHandwriting(false));
    }
  }

  private changeVisibilities(shapeKinds: ShapeKind[], visible: boolean) {
    const newVisibleShapeKinds = this.props.toolState.visibleShapeKinds.slice();
    for (const shapeKind of shapeKinds) {
      const index = newVisibleShapeKinds.indexOf(shapeKind);
      if (index !== -1) {
        if (!visible) {
          newVisibleShapeKinds.splice(index, 1);
        }
      } else {
        if (visible) {
          newVisibleShapeKinds.push(shapeKind);
        }
      }
    }
    this.props.onToolAction(DrawingToolAction.setVisibleShapeKinds(newVisibleShapeKinds));
  }

  private isVisibleDrawingNothing(): boolean {
    return this.props.toolState.visibleShapeKinds.length === 0 && !this.isHandwritingVisible();
  }

  private handleChangeAlpha = () => {
    // TODO: Get alpha channel from color picker.
    this.props.onToolAction(DrawingToolAction.setIsTransparent(
      !this.currentDrawingAttr().isTransparent),
    );
  }

  private handleChangeLineColor = () => {
    // For convinience, toggle the line color between black and random color.
    // TODO: Get line color from color picker.
    const newColor = (
      this.currentDrawingAttr().lineColor === 'black' ? createRandomColor() : 'black'
    );
    this.props.onToolAction(DrawingToolAction.setLineColor(newColor));
  }

  private handleChangeLineWidth = () => {
    let newWidth: number = 3;
    if (this.currentDrawingAttr().lineWidth === 3) {
      newWidth = 10;
    } else if (this.currentDrawingAttr().lineWidth === 10) {
      newWidth = 30;
    }
    this.props.onToolAction(DrawingToolAction.setLineWidth(newWidth));
  }

  private getLineWidthIcon = (): string => {
    let icon: string = 'dehaze';
    if (this.currentDrawingAttr().lineWidth === 10) {
      icon = 'view_stream';
    } else if (this.currentDrawingAttr().lineWidth === 30) {
      icon = 'stop';
    }
    return icon;
  }

  private handleChangeLineType = () => {
    let lineType: LineType = LineType.Solid;
    if (this.currentDrawingAttr().lineType === LineType.Solid) {
      lineType = LineType.Dotted;
    } else if (this.currentDrawingAttr().lineType === LineType.Dotted) {
      lineType = LineType.Dashed;
    }
    this.props.onToolAction(DrawingToolAction.setLineType(lineType));
  }

  private getLineTypeIcon = (): string => {
    switch (this.currentDrawingAttr().lineType) {
      case LineType.Dotted: return 'more_horiz';
      case LineType.Dashed: return 'power_input';
      case LineType.Solid:
      default:
        return 'remove';
    }
  }

  private handleChangeIsFilledAndFillColor = () => {
    // TODO: Get color from color picker.
    this.props.onToolAction(DrawingToolAction.setFillColor(createRandomColor()));
    this.props.onToolAction(DrawingToolAction.setIsFilled(!this.currentDrawingAttr().isFilled));
  }

  private handleChangeImageFile = () => {
    let selectedFile;

    if (this.imageFileInput === null) {
      selectedFile = null;
    } else {
      const { files } = this.imageFileInput;
      if (files === null || files.length === 0) {
        selectedFile = null;
      } else {
        selectedFile = files[0];
      }
    }

    this.props.onToolAction(DrawingToolAction.setImageToPaste(selectedFile));
  }

  private handleUndo = () => {
    if (this.props.canUndo) {
      this.props.onUndo();
    }
  }

  private handleRedo = () => {
    if (this.props.canRedo) {
      this.props.onRedo();
    }
  }

  private handleToggleMenu = () => {
    this.setState({ showFiguresMenu: !this.state.showFiguresMenu });
  }

  private handleToggleDrawingVisibilityMenu = () => {
    this.setState({ showDrawingVisibilityMenu: !this.state.showDrawingVisibilityMenu });
  }

  private currentDrawingAttr(): DrawingAttr {
    const { mode, drawingAttrs } = this.props.toolState;
    return drawingAttrs[mode];
  }

  public render(): React.ReactNode {
    const { mode } = this.props.toolState;
    const { fillColor, lineColor, isTransparent, isFilled } = this.currentDrawingAttr();

    const activeStyle: React.CSSProperties = {
      color: '#ff0',
    };

    const activeModeStyle: React.CSSProperties = {
      color: '#f60',
    };

    const activeModeStyleHorizontalLong = {
      ...activeModeStyle,
      transform: 'scale(1.3, 1)',
    };

    const activeModeStyleRotateAnti45 = {
      ...activeModeStyle,
      transform: 'rotate(-45deg)',
    };

    const inactiveStyle: React.CSSProperties = {
      color: '#fff',
    };

    const inactiveStyleHorizontalLong = {
      ...inactiveStyle,
      transform: 'scale(1.3, 1)',
    };

    const inactiveModeStyleRotateAnti45 = {
      ...inactiveStyle,
      transform: 'rotate(-45deg)',
    };

    const lineWidthIcon = this.getLineWidthIcon();
    const lineTypeIcon = this.getLineTypeIcon();

    return <div style={toolbarStyle}>
      <section style={sectionStyle}>
        <MdcIconButton
          onClick={this.handleUndo}
          className={classNames({ disabled: !this.props.canUndo })}
          title="元に戻す"
          ariaLabel="Undo">
          undo
        </MdcIconButton>
        <MdcIconButton
          onClick={this.handleRedo}
          className={classNames({ disabled: !this.props.canRedo })}
          title="やり直し"
          ariaLabel="Redo">
          redo
        </MdcIconButton>
        <MdcIconButton
          onClick={this.handleChangeLineColor}
          style={{ color: lineColor }}
          title="線の色"
          ariaLabel="LineColor">
          border_color
        </MdcIconButton>
        <MdcIconButton
          onClick={this.handleChangeIsFilledAndFillColor}
          style={isFilled ? { color: fillColor } : inactiveStyle}
          title="塗りつぶし"
          ariaLabel="Fill">
          format_color_fill
        </MdcIconButton>
        <MdcIconButton
          onClick={this.handleChangeLineWidth}
          style={inactiveStyle}
          title="線の太さ"
          ariaLabel="LineWidth">
          {lineWidthIcon}
        </MdcIconButton>
        <MdcIconButton
          onClick={this.handleChangeLineType}
          style={inactiveStyle}
          title="線の種類"
          aria-label="Line Type">
          {lineTypeIcon}
        </MdcIconButton>
        <MdcIconButton
          onClick={this.handleChangeAlpha}
          style={isTransparent ? activeStyle : inactiveStyle}
          title="半透過"
          ariaLabel="Alpha">
          flip
        </MdcIconButton>
        <ButtonSpacer />
        <MdcIconButton
          onClick={() => this.setMode(DrawingMode.Selection)}
          style={mode === DrawingMode.Selection ? activeModeStyle : inactiveStyle}
          title="選択"
          ariaLabel="Selection">
          border_clear
        </MdcIconButton>
        <MdcIconButton
          onClick={() => this.setMode(DrawingMode.ObjectRemoval)}
          style={mode === DrawingMode.ObjectRemoval ? activeModeStyle : inactiveStyle}
          title="オブジェクト消去"
          ariaLabel="Object removal">
          delete
        </MdcIconButton>
        <MdcIconButton
          onClick={() => this.confirmClearEverything()}
          style={inactiveStyle}
          title="オブジェクト全消去"
          ariaLabel="Clear Everything">
          delete_forever
        </MdcIconButton>
        <MdcIconButton
          onClick={() => this.setMode(DrawingMode.Handwriting)}
          style={mode === DrawingMode.Handwriting ? activeModeStyle : inactiveStyle}
          title="ペン"
          ariaLabel="Pen">
          brush
        </MdcIconButton>
        <MdcIconButton
          onClick={() => this.setMode(DrawingMode.Handerasing)}
          style={mode === DrawingMode.Handerasing ? activeModeStyle : inactiveStyle}
          title="消しゴム"
          ariaLabel="Eraser">
          vibration
        </MdcIconButton>
        <MdcIconButton onClick={this.handleToggleMenu}
          className="material-icons toggle"
          style={this.state.showFiguresMenu ? { color: '#ff0' } : {}}
          title="図形と画像"
          ariaLabel="Figures and image">
          dashboard
        </MdcIconButton>
        <MdcMenu
          isBottomToTop={true}
          isOpen={this.state.showFiguresMenu}>
          <li
            className="mdc-list-item"
            style={{ backgroundColor: '#303030' }}
            role="menuitem">
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.RectDrawing)}
              style={mode === DrawingMode.RectDrawing ?
                activeModeStyleHorizontalLong : inactiveStyleHorizontalLong}
              title="長方形"
              ariaLabel="Rectangle">
              {isFilled ? 'stop' : 'check_box_outline_blank'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.SquareDrawing)}
              style={mode === DrawingMode.SquareDrawing ? activeModeStyle : inactiveStyle}
              title="四角形"
              ariaLabel="Quadrangle">
              {isFilled ? 'stop' : 'check_box_outline_blank'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.OvalDrawing)}
              style={mode === DrawingMode.OvalDrawing ?
                activeModeStyleHorizontalLong : inactiveStyleHorizontalLong}
              title="楕円"
              ariaLabel="Oval">
              {isFilled ? 'fiber_manual_record' : 'radio_button_unchecked'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.CircleDrawing)}
              style={mode === DrawingMode.CircleDrawing ? activeModeStyle : inactiveStyle}
              title="円"
              ariaLabel="Circle">
              {isFilled ? 'fiber_manual_record' : 'radio_button_unchecked'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.TriangleDrawing)}
              style={
                mode === DrawingMode.TriangleDrawing ?
                  activeModeStyleHorizontalLong : inactiveStyleHorizontalLong
              }
              title="三角形"
              ariaLabel="Triangle">
              {isFilled ? 'eject' : 'change_history'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.EquilateralTriangleDrawing)}
              style={
                mode === DrawingMode.EquilateralTriangleDrawing ?
                  activeModeStyle : inactiveStyle
              }
              title="正三角形"
              ariaLabel="Triangle">
              {isFilled ? 'eject' : 'change_history'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.StickyDrawing)}
              style={
                mode === DrawingMode.StickyDrawing ? activeModeStyle : inactiveStyle
              }
              title="付箋"
              ariaLabel="Sticky">
              note
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.BalloonDrawing)}
              style={
                mode === DrawingMode.BalloonDrawing ? activeModeStyle : inactiveStyle
              }
              title="吹き出し"
              ariaLabel="Balloon">
              textsms
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.MultiVertexDrawing)}
              style={mode === DrawingMode.MultiVertexDrawing ? activeModeStyle : inactiveStyle}
              title="多角形"
              ariaLabel="Polygon">
              label_outline
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.AxisAlignedStraightLineDrawing)}
              style={
                mode === DrawingMode.AxisAlignedStraightLineDrawing ?
                  activeModeStyle : inactiveStyle
              }
              title="水平・垂直線"
              ariaLabel="AxisAlignedStraightLine">
              border_color
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.StraightLineDrawing)}
              style={
                mode === DrawingMode.StraightLineDrawing ?
                  activeModeStyleRotateAnti45 : inactiveModeStyleRotateAnti45
              }
              title="直線"
              ariaLabel="StraightLine">
              border_color
            </MdcIconButton>
          </li>
          <li className="mdc-list-item" role="menuitem">
            <input
              ref={el => this.imageFileInput = el}
              style={{ width: '100%' }}
              onChange={this.handleChangeImageFile}
              type="file" />
            <MdcIconButton
              onClick={() => this.setMode(DrawingMode.ImagePasting)}
              style={mode === DrawingMode.ImagePasting ?
                activeModeStyle : { color: '#000' }}
              title="画像ファイル"
              ariaLabel="Image file">
              image
            </MdcIconButton>
          </li>
        </MdcMenu>
        <MdcIconButton onClick={this.handleToggleDrawingVisibilityMenu}
          className="material-icons toggle"
          style={this.state.showDrawingVisibilityMenu ? { color: '#ff0' } : {}}
          title="書き込みの可視設定"
          ariaLabel="Visibility Setting For Drawings">
          no_sim
        </MdcIconButton>
        <MdcMenu
          isBottomToTop={true}
          isOpen={this.state.showDrawingVisibilityMenu}>
          <li
            className="mdc-list-item"
            style={{ backgroundColor: '#303030' }}
            role="menuitem">
            <MdcIconButton
              onClick={() => this.toggleHandwritingVisibility()}
              style={this.isHandwritingVisible() ? activeModeStyle : inactiveStyle}
              title="ペン"
              ariaLabel="Pen">
              brush
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Rect)}
              style={
                this.isVisible(ShapeKind.Rect) ?
                  activeModeStyleHorizontalLong : inactiveStyleHorizontalLong
              }
              title="長方形"
              ariaLabel="Rectangle">
              {isFilled ? 'stop' : 'check_box_outline_blank'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Square)}
              style={this.isVisible(ShapeKind.Square) ? activeModeStyle : inactiveStyle}
              title="正方形"
              ariaLabel="Quadrangle">
              {isFilled ? 'stop' : 'check_box_outline_blank'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Oval)}
              style={this.isVisible(ShapeKind.Oval) ?
                activeModeStyleHorizontalLong : inactiveStyleHorizontalLong}
              title="楕円"
              ariaLabel="Oval">
              {isFilled ? 'fiber_manual_record' : 'radio_button_unchecked'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Circle)}
              style={this.isVisible(ShapeKind.Circle) ? activeModeStyle : inactiveStyle}
              title="円"
              ariaLabel="Circle">
              {isFilled ? 'fiber_manual_record' : 'radio_button_unchecked'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Triangle)}
              style={this.isVisible(ShapeKind.Triangle) ?
                activeModeStyleHorizontalLong : inactiveStyleHorizontalLong
              }
              title="三角形"
              ariaLabel="Triangle">
              {isFilled ? 'eject' : 'change_history'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.EquilateralTriangle)}
              style={
                this.isVisible(ShapeKind.EquilateralTriangle) ? activeModeStyle : inactiveStyle
              }
              title="正三角形"
              ariaLabel="Triangle">
              {isFilled ? 'eject' : 'change_history'}
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Sticky)}
              style={this.isVisible(ShapeKind.Sticky) ? activeModeStyle : inactiveStyle}
              title="付箋"
              ariaLabel="Sticky">
              note
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Balloon)}
              style={this.isVisible(ShapeKind.Balloon) ? activeModeStyle : inactiveStyle}
              title="吹き出し"
              ariaLabel="Balloon">
              textsms
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Polygon)}
              style={this.isVisible(ShapeKind.Polygon) ? activeModeStyle : inactiveStyle}
              title="多角形"
              ariaLabel="Polygon">
              label_outline
            </MdcIconButton>
          </li>
          <li
            className="mdc-list-item"
            style={{ backgroundColor: '#303030' }}
            role="menuitem">
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.Polyline)}
              style={this.isVisible(ShapeKind.Polyline) ? activeModeStyle : inactiveStyle}
              title="折れ線"
              ariaLabel="Polygon">
              show_chart
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.AxisAlignedStraightLine)}
              style={
                this.isVisible(ShapeKind.AxisAlignedStraightLine)
                  ? activeModeStyle : inactiveStyle
              }
              title="水平・垂直線"
              ariaLabel="StraightLine">
              border_color
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.StraightLine)}
              style={
                this.isVisible(ShapeKind.StraightLine)
                  ? activeModeStyleRotateAnti45 : inactiveModeStyleRotateAnti45
              }
              title="直線"
              ariaLabel="StraightLine">
              border_color
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleVisibility(ShapeKind.ImageFile)}
              style={this.isVisible(ShapeKind.ImageFile) ? activeModeStyle : inactiveStyle}
              title="画像ファイル"
              ariaLabel="Image file">
              image
            </MdcIconButton>
            <MdcIconButton
              onClick={() => this.toggleAllDrawingVisibility()}
              style={this.isVisibleDrawingNothing() ? activeModeStyle : inactiveStyle}
              title="すべて隠す"
              ariaLabel="Hide all">
              not_interested
            </MdcIconButton>
          </li>
        </MdcMenu>
      </section>
    </div>;
  }
}

export default DrawingToolbar;
